import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  Baby,
  Bandage,
  BedDouble,
  ChevronRight,
  Clock,
  Dumbbell,
  Ear,
  GraduationCap,
  Layers,
  Maximize2,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Scale,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/otoplasty-ear-reshaping-surgery-mumbai";

const OTOPLASTY_FAQS = [
  {
    question: "At what age can children have otoplasty?",
    answer:
      "Surgery is appropriate from age 5 to 6, when the ear has reached approximately 85 to 90 percent of its adult size. Operating earlier risks interfering with ear development. There is no benefit to waiting beyond this age, and earlier correction prevents the social difficulties that prominent ears cause during school years.",
  },
  {
    question: "Is otoplasty painful?",
    answer:
      "Children are operated under general anesthesia and experience no pain during surgery. Adults are treated under local anesthesia and feel pressure but not pain. Post-operative discomfort is mild, described as tightness and soreness around the ears, and is managed with standard pain medication for the first 2 to 3 days.",
  },
  {
    question: "Will otoplasty results look natural?",
    answer:
      "Yes, when the degree of repositioning is proportionate to the patient's head size and ear anatomy. The most common cause of an operated appearance is overcorrection: ears pinned too flat against the skull. Dr. Jain sets the corrected position based on normal anatomical proportion, not maximum proximity to the head.",
  },
  {
    question: "Are otoplasty results permanent?",
    answer:
      "Yes. The sutures used to maintain the corrected cartilage position are permanent. The ears remain in their corrected position permanently. Early suture loosening in the first weeks is rare but can be addressed promptly if it occurs.",
  },
  {
    question: "Are there visible scars after ear reshaping surgery?",
    answer:
      "No. The incision is made in the natural crease behind the ear where it meets the scalp. It is not visible from the front and is concealed within the ear-scalp junction from all normal angles of view.",
  },
  {
    question: "Can otoplasty correct asymmetry between ears?",
    answer:
      "Yes. Each ear is assessed and treated independently. The goal is bilateral symmetry, which requires different degrees of correction on each side in most patients. No two ears are identical, and treating them the same way produces uneven results.",
  },
  {
    question: "How long does recovery take after otoplasty?",
    answer:
      "Most patients return to normal daily activity within 1 to 2 weeks. Children return to school within a week. Contact sports and activities with physical ear contact require a 4 to 6 week break. A soft protective headband is worn at night for 4 to 6 weeks after the dressing is removed.",
  },
  {
    question: "Can otoplasty be combined with other procedures?",
    answer:
      "Yes, though it is less commonly combined with facial procedures than other surgeries. Adults occasionally combine otoplasty with rhinoplasty or other facial procedures when multiple concerns are being addressed simultaneously. For children, otoplasty is almost always performed as a standalone procedure.",
  },
];

const otoplastyStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Otoplasty (ear reshaping surgery)",
      alternateName: ["Ear reshaping", "Ear pinning", "Prominent ear correction", "Bat ear correction"],
      description:
        "Surgical repositioning and reshaping of the ear for protruding, asymmetric, oversized, or congenitally misshapen ears. Appropriate for children from approximately age 5 to 6 (when the ear has reached ~85–90% adult size) and adults of any age; incisions hidden behind the ear. Common searches include ear surgery for children and protruding ear correction for kids.",
      procedureType: "Surgical",
      minimumAge: {
        "@type": "QuantitativeValue",
        value: 5,
        unitText: "years",
      },
      bodyLocation: {
        "@type": "AnatomicalStructure",
        name: "Ear",
      },
      followup:
        "Days 1–3: dressing, mild discomfort. Days 5–7: dressing removed; night headband 4–6 weeks. Week 2: desk work / school; avoid ear contact sports 4–6 weeks. Weeks 4–6: ears settle; swelling resolves.",
      howPerformed:
        "1–2 hours; children under general anesthesia, adults typically local with sedation; incision in posterior ear crease; cartilage reshaping, scoring, antihelical fold recreation; permanent sutures; dressing or headband; same-day discharge in most cases.",
      url: PAGE_URL,
      additionalProperty: [
        { "@type": "PropertyValue", name: "Procedure category", value: "Surgical" },
        { "@type": "PropertyValue", name: "Pediatric eligibility", value: "From age 5–6 when ear size appropriate" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: OTOPLASTY_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
  ],
};

function OtoplastyJsonLd() {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(otoplastyStructuredData) }} />
  );
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Face", href: "/#services" },
    { name: "Otoplasty", href: "/otoplasty-ear-reshaping-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Face</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-5">
              Ear Reshaping Surgery
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg text-deep-navy/70 mb-8">
              Otoplasty for Protruding &amp; Asymmetric Ears by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Otoplasty" icon="◇" size="hero"  imageSrc={HERO.otoplasty} imageAlt="Otoplasty"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What is Otoplasty?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Otoplasty is ear reshaping surgery. It repositions protruding ears closer to the head, reshapes misshapen
              cartilage, corrects size asymmetry, and addresses congenital or post-traumatic ear deformity. The procedure is
              performed on children from age 5 and on adults of any age. Results are permanent.
            </p>
            <p>
              Ears are rarely the first feature someone notices on a face, but when they protrude significantly or sit
              asymmetrically, they become the dominant visual element. Patients avoid certain hairstyles, feel self-conscious
              in photos, and carry an awareness of their ears into every social situation. For children, prominent ears are a
              frequent target for teasing, and the psychological impact of that experience is well-documented. Correcting the
              problem early removes it before it compounds.
            </p>
            <p>
              Dr. Vicky Jain performs otoplasty for both children and adults. The surgery takes 1 to 2 hours, scars
              are hidden behind the ear, and most patients return to normal activity within 1 to 2 weeks. The change is
              immediate and permanent.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">
              Dr. Jain&apos;s Approach to Ear Reshaping Surgery
            </h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The ear is a structurally complex unit of cartilage, skin, and soft tissue. Effective otoplasty requires
                understanding which anatomical element is causing the protrusion or asymmetry, then correcting that element
                specifically. The most common cause of prominent ears is an underdeveloped antihelical fold, the curved ridge of
                cartilage that gives the ear its normal contoured shape. When this fold is absent or flattened, the ear
                projects outward. Dr. Jain reshapes or recreates this fold using suture techniques and cartilage scoring,
                restoring the natural ear contour.
              </p>
              <p>
                Symmetry is the primary technical goal. Both ears are assessed and treated independently because no two ears
                are identical, even in the same patient. Dr. Jain marks and plans the degree of correction on each side before
                making any incision, so the result is balanced rather than matched to a template. Overcorrection, producing
                ears that are pinned too flat against the head, is as problematic as undercorrection. The goal is ears that
                look natural, not operated on.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatCorrectsSection() {
  const cards = [
    {
      title: "Protruding Ears",
      icon: Ear,
      copy: "The most common reason patients seek otoplasty. Ears that protrude more than 2 centimeters from the side of the head, or at an angle greater than 25 degrees, are repositioned closer to the skull through cartilage reshaping and permanent sutures.",
    },
    {
      title: "Absent or Flat Antihelical Fold",
      icon: Layers,
      copy: "The antihelical fold is the inner curved ridge of the ear that creates the ear's natural contour. When this fold is underdeveloped, the ear loses its shape and projects outward. Dr. Jain recreates this fold through cartilage work to restore a natural ear profile.",
    },
    {
      title: "Ear Asymmetry",
      icon: Scale,
      copy: "Ears that differ in position, projection, or shape between sides create visible facial imbalance. Otoplasty corrects each ear independently to produce bilateral symmetry.",
    },
    {
      title: "Large Ears",
      icon: Maximize2,
      copy: "Ears that are disproportionately large relative to the head can be reduced in size through cartilage excision. Ear reduction is frequently combined with repositioning for a complete result.",
    },
    {
      title: "Congenital Ear Deformity",
      icon: Baby,
      copy: "Some patients are born with ears that are misshapen, folded, or partially formed. Reconstructive otoplasty addresses these structural abnormalities and improves both the appearance and the anatomical form of the ear.",
    },
    {
      title: "Post-Traumatic Ear Deformity",
      icon: Bandage,
      copy: "Injury to the ear from accidents, sports, or piercings can alter its shape permanently. Dr. Jain corrects trauma-related deformity through cartilage repair and reshaping appropriate to what the injury has caused.",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="otoplasty-corrects-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">What Ear Reshaping Surgery Corrects</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card, index) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]" data-testid={`otoplasty-corrects-card-${index}`}>
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PROCEDURE_TYPES = [
  {
    title: "Ear Pinning Surgery",
    body:
      "The most commonly performed otoplasty. The ear is repositioned closer to the head by reshaping or recreating the antihelical fold and securing the corrected position with permanent sutures placed through an incision behind the ear. Best for patients whose primary concern is protrusion.",
  },
  {
    title: "Ear Cartilage Reshaping",
    body:
      "Cartilage scoring or excision is used to alter the shape of the ear when protrusion is caused by cartilage structure rather than fold position alone. Sutures hold the reshaped cartilage in its corrected position. Used when the ear's overall contour, not just its position, needs to be improved.",
  },
  {
    title: "Ear Reduction Surgery",
    body:
      "Reduces the overall size of ears that are disproportionately large. Cartilage and skin are removed and the ear is reconstructed at a smaller scale. Technically more involved than repositioning alone and requires precise planning to maintain natural ear anatomy at a reduced size.",
  },
  {
    title: "Reconstructive Otoplasty",
    body:
      "Addresses congenital deformities such as cup ear, lop ear, or cryptotia, where the ear has developed abnormally. The approach depends entirely on the specific deformity. Some cases require cartilage grafting from other sites to build missing ear structure.",
  },
];

function ProcedureTypesSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="otoplasty-procedure-types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-10 text-center">Types of Otoplasty Procedures</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {PROCEDURE_TYPES.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgeryPerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">How Otoplasty is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 1 to 2 hours. Children are operated under general anesthesia. Adults are treated under local
              anesthesia with sedation in most cases, though general anesthesia is available for patients who prefer it.
            </p>
            <p>
              Dr. Jain makes an incision in the natural crease behind the ear where it meets the scalp. Through this
              incision, the cartilage is accessed, reshaped, or scored as needed, and repositioned using permanent sutures that
              hold the corrected ear position. The skin is closed carefully and the incision sits entirely within the
              posterior ear crease, making the scar invisible from the front and not visible from the side in normal social
              settings.
            </p>
            <p>
              A soft dressing or headband is applied immediately after surgery to protect the ears and maintain the corrected
              position during the first week of healing. Most patients go home the same day. The dressing is removed at the
              first post-operative visit within 5 to 7 days.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Mild discomfort, swelling, and tightness around the ears. The dressing stays on. Pain is managed with standard medication.",
      icon: BedDouble,
    },
    {
      period: "Days 5 to 7",
      description:
        "Dressing is removed at the post-operative visit. The ears are visible and the correction is immediately apparent. A soft headband is worn at night for 4 to 6 weeks to protect the position during sleep.",
      icon: Monitor,
    },
    {
      period: "Week 2",
      description:
        "Most adults return to desk work and normal daily activity. Children can return to school. Contact sports and activities with ear contact risk are avoided for 4 to 6 weeks.",
      icon: Dumbbell,
    },
    {
      period: "Weeks 4 to 6",
      description:
        "The ears settle into their final position. Residual swelling resolves. The result is permanent and behaves as the patient's natural ear anatomy from this point forward.",
      icon: Layers,
    },
  ];

  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function OtoplastyForChildrenSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="otoplasty-children-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Paediatric Otoplasty</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Otoplasty for Children: When and Why</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The ear reaches approximately 85 to 90 percent of its adult size by age 5 to 6. This is the earliest point at
              which otoplasty is appropriate and the reason surgery is commonly performed in this age group. Correcting
              prominent ears before a child starts school removes the physical basis for teasing before it begins. Studies
              consistently show that children who undergo otoplasty early report better social integration and self-confidence
              than those who delay.
            </p>
            <p>
              General anesthesia is used for children, making the procedure comfortable and stress-free for the patient.
              Recovery is straightforward. Most children return to school within a week. Contact sports require a 4 to 6 week
              break. Parents are briefed in detail on post-operative care, dressing management, and what to expect at each
              stage of healing. The result is permanent and grows with the child as the remaining ear development completes.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
            Who Is a Good Candidate for Otoplasty?
          </h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Otoplasty is appropriate for children from age 5 and for adults of any age. There is no upper age limit. The
              procedure is equally effective across all adult age groups and the recovery profile does not change
              significantly with age. Adults who have lived with prominent or asymmetric ears for decades and are seeking
              correction for the first time are common and entirely suitable candidates.
            </p>
            <p>
              Good candidates have a specific structural concern with one or both ears, are in good general health, and have
              realistic expectations about the degree of change the procedure produces. Otoplasty moves the ears into a normal
              position and proportion relative to the head. It does not alter the fundamental character of the face or
              produce a dramatic transformation of appearance. The goal is for the ears to stop being noticed, not to become a
              new focal point. Patients who understand this distinction get the most consistent satisfaction from the result.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Ear" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {OTOPLASTY_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`item-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Otoplasty Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain examines the ear structure, cartilage position, degree of protrusion, and symmetry
          between sides to determine the technique and degree of correction appropriate for your anatomy. For children, both the
          patient and parents are fully briefed on what the procedure involves, what to expect during recovery, and what the
          result will look like. You leave with a clear plan and no unanswered questions.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Rhinoplasty",
      description: "Refine nasal shape alongside ear correction for complete facial balance.",
      href: "/rhinoplasty-in-mumbai",
    },
    {
      name: "Facelift",
      description: "Complete facial rejuvenation for adult patients with ear concerns alongside facial aging.",
      href: "/facelift-surgery-mumbai",
    },
    {
      name: "Chin Augmentation",
      description: "Strengthen lower face proportion to complement corrected ear position.",
      href: "/chin-augmentation-in-mumbai",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function OtoplastyPage() {
  return (
    <>
      <SEO
        title="Ear Reshaping Surgery | Otoplasty by Dr. Vicky Jain"
        description="Protruding or asymmetric ears affect confidence at any age. Dr. Vicky Jain performs otoplasty for children and adults with natural results. Book today."
        canonical="/otoplasty-ear-reshaping-surgery-mumbai"
      />
      <OtoplastyJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <WhatCorrectsSection />
      <ProcedureTypesSection />
      <HowSurgeryPerformedSection />
      <RecoveryTimelineSection />
      <OtoplastyForChildrenSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
